import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { MoreHorizontal, Lock, Users, Share2 } from "lucide-react";
import FolderTabs, { FolderTabKey, FolderTabCounts } from "./FolderTabs";
import NewFolderMenu, { NewFolderMenuAction } from "./NewFolderMenu";
import LinkForm from "./LinkForm";
import DocumentForm from "./DocumentForm";
import DocumentRenameForm from "./DocumentRenameForm";
import DocumentMoveForm from "./DocumentMoveForm";
import FolderForm from "./FolderForm";
import ConfirmModal from "./ConfirmModal";
import ShareModal from "./ShareModal";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";
import LimitedAccessBanner from "./LimitedAccessBanner";
import { User, loadUsers } from "./user-display";
import { RowKind, RowKebab } from "./category-rows";
import { CatTableBody, Row, withPinnedFirst } from "./CatTableBody";
import { reorderPinned } from "./dnd-helpers";

interface FolderMeta {
    id: string;
    name: string;
    description: string | null;
    categoryId: string;
    icon: string | null;
    color: string | null;
    sortMode: string;
    isPinned: boolean;
    sortOrder: number;
    updatedAt: string;
    createdBy: string | null;
}

interface Page {
    id: string;
    title: string;
    status: string;
    updatedAt: string;
    isPinned: boolean;
    folderId: string | null;
    createdBy: string | null;
    ownerId: string | null;
}

interface LinkItem {
    id: string;
    title: string;
    url: string;
    description: string | null;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface DocumentItem {
    id: string;
    filename: string;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface Category {
    id: string;
    name: string;
    icon: string | null;
    color: string | null;
    accessLevel?: string;
}

interface Props {
    folderId: string;
    categoryId: string;
    categories: Category[];
    isAdmin: boolean;
    canManage: boolean;
    currentUserId: string | null;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onCreatePage: (categoryId: string, folderId: string | null) => void;
    onEditFolder: (folder: FolderMeta) => void;
    onDeleteFolder: (folder: FolderMeta) => void;
    onSelectCategory: (categoryId: string) => void;
    treeVersion: number;
}

// Row shape and rendering are shared with CategoryContentView — see CatTableBody.

export default function FolderView(props: Props) {
    const {
        folderId, categoryId, categories, isAdmin, canManage, currentUserId,
        onSelectPage, onCreatePage,
        onDeleteFolder, onSelectCategory, treeVersion,
    } = props;

    const [folder, setFolder] = useState<FolderMeta | null>(null);
    const [pages, setPages] = useState<Page[]>([]);
    const [links, setLinks] = useState<LinkItem[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [users, setUsers] = useState<Map<string, User>>(new Map());
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<FolderTabKey>("everything");
    const [reloadKey, setReloadKey] = useState(0);
    const [openKebabId, setOpenKebabId] = useState<string | null>(null);

    const [stripKebabOpen, setStripKebabOpen] = useState(false);
    const stripKebabRef = useRef<HTMLDivElement>(null);

    const [showLink, setShowLink] = useState(false);
    const [showDocument, setShowDocument] = useState(false);
    const [editLink, setEditLink] = useState<LinkItem | null>(null);
    const [deleteLink, setDeleteLink] = useState<LinkItem | null>(null);
    const [renameDoc, setRenameDoc] = useState<DocumentItem | null>(null);
    const [moveDoc, setMoveDoc] = useState<DocumentItem | null>(null);
    const [deleteDoc, setDeleteDoc] = useState<DocumentItem | null>(null);
    const [renameFolder, setRenameFolder] = useState<FolderMeta | null>(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [deletePage, setDeletePage] = useState<Page | null>(null);

    const category = categories.find(c => c.id === categoryId);

    useEffect(() => {
        loadUsers().then(us => {
            const m = new Map<string, User>();
            us.forEach(u => m.set(u.id, u));
            setUsers(m);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!stripKebabOpen) return;
        const handler = (e: MouseEvent) => {
            if (!stripKebabRef.current?.contains(e.target as Node)) setStripKebabOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [stripKebabOpen]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            axios.get(`/api/manuals/folders/${folderId}`),
            axios.get(`/api/manuals/folders/${folderId}/pages`),
            axios.get(`/api/manuals/folders/${folderId}/documents`),
            axios.get(`/api/manuals/folders/${folderId}/links`),
        ]).then(([fRes, pRes, dRes, lRes]) => {
            if (cancelled) return;
            if (!fRes.data.success) { toast.error("Folder not found"); setLoading(false); return; }
            setFolder(fRes.data.item);
            setPages(pRes.data.success ? pRes.data.items : []);
            setDocuments(dRes.data.success ? dRes.data.items : []);
            setLinks(lRes.data.success ? lRes.data.items : []);
            setLoading(false);
        }).catch(() => {
            if (cancelled) return;
            toast.error("Failed to load folder");
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [folderId, treeVersion, reloadKey]);

    function bumpReload() { setReloadKey(k => k + 1); }

    function handleNew(action: NewFolderMenuAction) {
        switch (action) {
            case "page": onCreatePage(categoryId, folderId); break;
            case "link": setShowLink(true); break;
            case "document": setShowDocument(true); break;
        }
    }

    const counts: FolderTabCounts = useMemo(() => ({
        pages: pages.length,
        links: links.length,
        documents: documents.length,
    }), [pages, links, documents]);

    if (loading) return <div className="m-loading"><CustomLoading /></div>;
    if (!folder) return null;

    const pageRows: Row[] = withPinnedFirst(pages.map(p => {
        // admins delete any page; editors may delete pages they own
        const canEdit = isAdmin || (canManage && !!currentUserId && p.ownerId === currentUserId);
        return {
            kind: "page",
            id: p.id,
            title: p.title,
            modifiedAt: p.updatedAt,
            ownerId: p.createdBy,
            onClick: () => onSelectPage(p.id, categoryId),
            isPinned: p.isPinned,
            rowKebab: canEdit ? (
                <RowKebab
                    isOpen={openKebabId === p.id}
                    onToggle={() => setOpenKebabId(id => id === p.id ? null : p.id)}
                    items={[
                        { label: "Delete", onClick: () => { setDeletePage(p); setOpenKebabId(null); }, danger: true },
                    ]}
                />
            ) : undefined,
        };
    }));

    const linkRows: Row[] = withPinnedFirst(links.map(l => {
        return {
            kind: "link",
            id: l.id,
            title: l.title,
            modifiedAt: l.updatedAt || l.createdAt,
            ownerId: l.createdBy,
            onClick: () => window.open(l.url, "_blank"),
            isPinned: l.isPinned,
            subtext: l.url,
            description: l.description,
            rowKebab: canManage ? (
                <RowKebab
                    isOpen={openKebabId === l.id}
                    onToggle={() => setOpenKebabId(id => id === l.id ? null : l.id)}
                    items={[
                        { label: "Edit", onClick: () => { setEditLink(l); setOpenKebabId(null); } },
                        ...(isAdmin ? [{ label: "Delete", onClick: () => { setDeleteLink(l); setOpenKebabId(null); }, danger: true }] : []),
                    ]}
                />
            ) : undefined,
        };
    }));

    const documentRows: Row[] = withPinnedFirst(documents.map(d => {
        return {
            kind: "document",
            id: d.id,
            title: d.filename,
            modifiedAt: d.updatedAt || d.createdAt,
            ownerId: d.createdBy,
            onClick: () => window.location.assign(`/api/manuals/documents/${d.id}/download`),
            isPinned: d.isPinned,
            rowKebab: canManage ? (
                <RowKebab
                    isOpen={openKebabId === d.id}
                    onToggle={() => setOpenKebabId(id => id === d.id ? null : d.id)}
                    items={[
                        { label: "Rename", onClick: () => { setRenameDoc(d); setOpenKebabId(null); } },
                        { label: "Move", onClick: () => { setMoveDoc(d); setOpenKebabId(null); } },
                        ...(isAdmin ? [{ label: "Delete", onClick: () => { setDeleteDoc(d); setOpenKebabId(null); }, danger: true }] : []),
                    ]}
                />
            ) : undefined,
        };
    }));

    // Optimistic like the category grid: drop lands immediately, a rejected save reloads.
    async function handleReorder(kind: RowKind, orderedIds: string[]) {
        const rank = new Map(orderedIds.map((id, i) => [id, i]));
        const sortPinned = <T extends { id: string; isPinned: boolean }>(items: T[]): T[] => {
            const pinned = items.filter(i => i.isPinned && rank.has(i.id))
                .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
            return [...pinned, ...items.filter(i => !(i.isPinned && rank.has(i.id)))];
        };

        switch (kind) {
            case "page": setPages(sortPinned); break;
            case "link": setLinks(sortPinned); break;
            case "document": setDocuments(sortPinned); break;
            default: return;
        }

        if (!(await reorderPinned(kind, orderedIds))) bumpReload();
    }

    let visibleRows: Row[] = [];
    switch (activeTab) {
        case "everything": visibleRows = [...pageRows, ...linkRows, ...documentRows]; break;
        case "pages": visibleRows = pageRows; break;
        case "links": visibleRows = linkRows; break;
        case "documents": visibleRows = documentRows; break;
    }

    function emptyMsg(tab: FolderTabKey): string {
        switch (tab) {
            case "everything": return "No items yet";
            case "pages": return "No pages yet";
            case "links": return "No links yet";
            case "documents": return "No documents yet";
        }
    }

    // Admins manage any folder; a non-admin Editor only one they created.
    const canEditFolder = isAdmin || (canManage && !!currentUserId && folder.createdBy === currentUserId);

    const pills: BreadcrumbPill[] = [
        {
            kind: "category",
            label: category?.name || "Category",
            iconName: category?.icon || null,
            iconColor: category?.color || null,
            onClick: () => onSelectCategory(categoryId),
        },
        {
            kind: "folder",
            label: folder.name,
            iconName: folder.icon || null,
            iconColor: folder.color || null,
        },
    ];

    const isRestricted = category?.accessLevel === "restricted";

    const headerRight = (
        <>
            <span className="m-cat-strip-access">
                {isRestricted ? <Lock size={13} /> : <Users size={13} />}
                {isRestricted ? "Invite only" : "Everyone"}
            </span>
            <button className="m-topbar-btn" onClick={() => setShareOpen(true)}>
                <Share2 size={14} /> Share
            </button>
            {canEditFolder && (
                <div ref={stripKebabRef} style={{ position: "relative" }}>
                    <button
                        className="m-cat-strip-kebab"
                        title="More actions"
                        onClick={e => { e.stopPropagation(); setStripKebabOpen(o => !o); }}
                    >
                        <MoreHorizontal size={16} />
                    </button>
                    {stripKebabOpen && (
                        <div className="m-cat-kebab-menu">
                            <button onClick={() => { setStripKebabOpen(false); setRenameFolder(folder); }}>
                                Rename
                            </button>
                            {isAdmin && (
                                <button
                                    className="m-cat-kebab-danger"
                                    onClick={() => { setStripKebabOpen(false); onDeleteFolder(folder); }}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div className="m-cat-page">
            <Breadcrumb pills={pills} right={headerRight} />

            <LimitedAccessBanner categoryId={categoryId} accessLevel={category?.accessLevel} />

            <div className="m-cat-tabs-row">
                <FolderTabs active={activeTab} counts={counts} onChange={setActiveTab} />
                {canManage && <NewFolderMenu onSelect={handleNew} />}
            </div>

            <div className={`m-cat-table${activeTab === "links" ? " m-cat-table-links" : ""}`}>
                <div className="m-cat-table-head">
                    <div>Name</div>
                    {activeTab === "links" && <div>Description</div>}
                    <div>Last modified</div>
                    <div>Owner</div>
                </div>
                {visibleRows.length === 0 ? (
                    <div className="m-cat-empty-row">{emptyMsg(activeTab)}</div>
                ) : (
                    <CatTableBody
                        rows={visibleRows}
                        showDescription={activeTab === "links"}
                        users={users}
                        canReorder={isAdmin}
                        onReorder={handleReorder}
                    />
                )}
            </div>

            {showLink && (
                <LinkForm
                    categoryId={categoryId}
                    categoryName={category?.name || ""}
                    onSave={() => { setShowLink(false); bumpReload(); }}
                    onCancel={() => setShowLink(false)}
                />
            )}
            {showDocument && (
                <DocumentForm
                    categoryId={categoryId}
                    categoryName={category?.name || ""}
                    onSave={() => { setShowDocument(false); bumpReload(); }}
                    onCancel={() => setShowDocument(false)}
                />
            )}
            {editLink && (
                <LinkForm
                    categoryId={categoryId}
                    categoryName={category?.name || ""}
                    linkId={editLink.id}
                    initialTitle={editLink.title}
                    initialUrl={editLink.url}
                    initialDescription={editLink.description ?? ""}
                    onSave={() => { setEditLink(null); bumpReload(); }}
                    onCancel={() => setEditLink(null)}
                />
            )}
            {deleteLink && (
                <ConfirmModal
                    title="Delete link"
                    message={`Delete "${deleteLink.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        axios.delete(`/api/manuals/links/${deleteLink.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success("Link deleted");
                                    setDeleteLink(null);
                                    bumpReload();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(err => toast.error(err.response?.data?.message || "Failed to delete"));
                    }}
                    onCancel={() => setDeleteLink(null)}
                />
            )}
            {renameDoc && (
                <DocumentRenameForm
                    doc={renameDoc}
                    onSave={() => { setRenameDoc(null); bumpReload(); }}
                    onCancel={() => setRenameDoc(null)}
                />
            )}
            {moveDoc && (
                <DocumentMoveForm
                    doc={moveDoc}
                    categories={categories}
                    onSave={() => { setMoveDoc(null); bumpReload(); }}
                    onCancel={() => setMoveDoc(null)}
                />
            )}
            {deleteDoc && (
                <ConfirmModal
                    title="Delete document"
                    message={`Delete "${deleteDoc.filename}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        axios.delete(`/api/manuals/documents/${deleteDoc.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success("Document deleted");
                                    setDeleteDoc(null);
                                    bumpReload();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(err => toast.error(err.response?.data?.message || "Failed to delete"));
                    }}
                    onCancel={() => setDeleteDoc(null)}
                />
            )}
            {renameFolder && (
                <FolderForm
                    folder={renameFolder}
                    categoryId={categoryId}
                    onSave={() => { setRenameFolder(null); bumpReload(); }}
                    onCancel={() => setRenameFolder(null)}
                />
            )}
            {deletePage && (
                <ConfirmModal
                    title="Delete page"
                    message={`Delete "${deletePage.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        axios.delete(`/api/manuals/pages/${deletePage.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success("Page deleted");
                                    setDeletePage(null);
                                    bumpReload();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(err => toast.error(err.response?.data?.message || "Failed to delete"));
                    }}
                    onCancel={() => setDeletePage(null)}
                />
            )}

            {shareOpen && folder && (
                <ShareModal
                    kind="folder"
                    label={folder.name}
                    url={window.location.href}
                    iconName={folder.icon}
                    iconColor={folder.color}
                    isRestricted={isRestricted}
                    onClose={() => setShareOpen(false)}
                />
            )}
        </div>
    );
}
